import _ from 'lodash';
import TZDate from './TZDate';

export const OPENED = 'OPENED';
export const CLOSED = 'CLOSED';

export const INVALID_RANGE = 'INVALID_RANGE';
export const INVALID_TIME_STRING = 'INVALID_TIME_STRING';
export const INVALID_ORDER = 'INVALID_ORDER';
export const OVERLAPPING_RANGE = 'OVERLAPPING_RANGE';

function partitionArray(arr, size) {
  let partition = [];
  return arr.reduce(
    function(acc, v) {
      partition.push(v);
      if (partition.length === size) {
        acc.push(partition);
        partition = [];
      }
      return acc;
    },
    []
  );
}

function createRange(date, { from, to }) {
  return {
    from: date.setTime(from),
    to: date.setTime(to)
  };
}

export function createRanges(date, openingHours) {
  return (openingHours || []).map(fromTo => createRange(date, fromTo));
}

function getSpecialOpeningHoursByDay(specialOpeningHours, zoneInfo) {
  const startDate = TZDate.createFromDateAndTimeString(
    specialOpeningHours.start,
    '00:00',
    zoneInfo
  );
  const endDate = TZDate.createFromDateAndTimeString(
    specialOpeningHours.end,
    '00:00',
    zoneInfo
  );

  let currentDate = startDate.clone();
  const openingHoursByDay = {};

  if (endDate.isBefore(startDate)) {
    return openingHoursByDay;
  }

  do {
    const dayStr = currentDate.getLocalDateString();
    openingHoursByDay[dayStr] = createRanges(
      currentDate,
      specialOpeningHours.openingHours
    );
    currentDate = currentDate.addDays(1);
  } while (currentDate.isSameOrBefore(endDate));

  return openingHoursByDay;
}

function getSpecialRangesByDate(allSpecialOpeningHours, zoneInfo) {
  return allSpecialOpeningHours
    .map(specialOpeningHours =>
      getSpecialOpeningHoursByDay(specialOpeningHours, zoneInfo))
    .reduce((memo, rangesByDate) => ({ ...memo, ...rangesByDate }), {});
}

function getLastDate(specialRangesByDayObj, zoneInfo, nowDate) {
  const specialDates = Object.keys(specialRangesByDayObj);
  let lastSpecialDate;

  if (specialDates.length) {
    specialDates.sort();
    lastSpecialDate = TZDate.createFromDateAndTimeString(
      specialDates[specialDates.length - 1],
      '00:00',
      zoneInfo
    );
  } else {
    lastSpecialDate = nowDate.clone();
  }

  return lastSpecialDate.addDays(8, 'days');
}

function flattenRanges(ranges) {
  return ranges.reduce(
    function(memo, range) {
      memo.push(range.from, range.to);
      return memo;
    },
    []
  );
}

// transforms ranges like this:
// [{ from: '08:00', to: '12:00' }, { from: '12:00', to: '18:00' }]
// to:
// [{ from: '08:00', to: '18:00' }]
function normalizeRanges(ranges) {
  const timeSpots = flattenRanges(ranges);

  const timeSpotsCount = timeSpots.reduce(
    function(memo, timeSpot) {
      const dateStr = timeSpot.getLocalDateTimeString();
      if (!(dateStr in memo)) {
        memo[dateStr] = 0;
      } else {
        memo[dateStr]++;
      }
      return memo;
    },
    {}
  );

  const timeSpotsWithoutDuplicates = timeSpots.filter(function(timeSpot) {
    const dateStr = timeSpot.getLocalDateTimeString();
    return timeSpotsCount[dateStr] === 0;
  });

  return partitionArray(timeSpotsWithoutDuplicates, 2).map(([from, to]) => ({
    from,
    to
  }));
}

function getOpeningStateFromRanges(ranges, zoneInfo, nowDate) {
  const timeSpots = flattenRanges(ranges);

  if (timeSpots.length === 2) {
    // handle the case when a store is opened 24/7
    let oneWeekLater = timeSpots[0].addDays(7);
    if (timeSpots[1].isSameOrAfter(oneWeekLater)) {
      return {
        status: OPENED
      };
    }
  }

  for (let i = 1; i < timeSpots.length; i++) {
    const currentTimeSpot = timeSpots[i];
    const prevTimeSpot = timeSpots[i - 1];

    if (!nowDate.isBetween(prevTimeSpot, currentTimeSpot)) {
      continue;
    }

    if (i % 2) {
      // the store is currently opened
      return {
        status: OPENED,
        closingDate: currentTimeSpot
      };
    } else {
      // the store is currently closed
      let state = {
        status: CLOSED,
        openingDate: currentTimeSpot
      };

      return state;
    }
  }

  let state = {
    status: CLOSED
  };

  if (timeSpots.length && nowDate.isBefore(timeSpots[0])) {
    state.openingDate = timeSpots[0];
  }

  return state;
}

export function getOpeningState(
  zoneInfo,
  openingHoursByDay,
  allSpecialOpeningHours = [],
  nowDate = new TZDate(Date.now(), zoneInfo)
) {
  const specialRangesByDate = getSpecialRangesByDate(
    allSpecialOpeningHours,
    zoneInfo
  );

  const lastDate = getLastDate(specialRangesByDate, zoneInfo, nowDate);
  let currentDate = nowDate.clone();
  const ranges = [];

  do {
    const dateStr = currentDate.getLocalDateString();

    if (dateStr in specialRangesByDate) {
      ranges.push(...specialRangesByDate[dateStr]);
    } else {
      const day = currentDate.getDayString().toLowerCase();
      ranges.push(
        ...createRanges(currentDate, openingHoursByDay[day].openingHours || [])
      );
    }

    currentDate = currentDate.addDays(1);
  } while (currentDate.isSameOrBefore(lastDate));

  return getOpeningStateFromRanges(normalizeRanges(ranges), zoneInfo, nowDate);
}

function getOverlappingRangesErrors(openingHours) {
  const overlapping = [];
  const nowDate = new TZDate();

  for (let i = 0; i < openingHours.length; i++) {
    if (getErrorsForFromTo(openingHours[i], i).length) {
      continue;
    }

    for (let y = 0; y < openingHours.length; y++) {
      if (i === y || getErrorsForFromTo(openingHours[y], y).length) {
        continue;
      }

      const range = createRange(nowDate, openingHours[i]);
      const comparedRange = createRange(nowDate, openingHours[y]);

      if (
        range.from.isBetween(comparedRange.from, comparedRange.to) ||
        range.to.isBetween(comparedRange.from, comparedRange.to)
      ) {
        overlapping.push(i);
        break;
      }
    }
  }

  return overlapping.map(idx => ({ path: `${idx}`, type: OVERLAPPING_RANGE }));
}

function isValidRange({ from, to }) {
  return to.isAfter(from);
}

function isValidTimeString(timeStr) {
  const pieces = timeStr.split(':');

  if (pieces.length !== 2 || pieces[0].length !== 2 || pieces[1].length !== 2) {
    return false;
  }

  const hours = parseInt(pieces[0], 10);
  const minutes = parseInt(pieces[1], 10);

  return !((hours === 24 && minutes !== 0) ||
    (hours < 0 || hours > 24) ||
    (minutes < 0 || minutes > 59));
}

function getErrorsForFromTo(fromTo, idx) {
  const nowDate = new TZDate();
  const errors = [];

  const fromIsValid = isValidTimeString(fromTo.from);
  const toIsValid = isValidTimeString(fromTo.to);

  if (!fromIsValid) {
    errors.push({
      path: `${idx}.from`,
      type: INVALID_TIME_STRING
    });
  }

  if (!toIsValid) {
    errors.push({
      path: `${idx}.to`,
      type: INVALID_TIME_STRING
    });
  }

  if (fromIsValid && toIsValid) {
    const range = createRange(nowDate, fromTo);

    if (!isValidRange(range)) {
      errors.push({
        path: `${idx}`,
        type: INVALID_RANGE
      });
    }
  }

  return errors;
}

export function validateOpeningHours(openingHours) {
  const errors = openingHours.reduce(
    function(errors, fromTo, idx) {
      return [...errors, ...getErrorsForFromTo(fromTo, idx)];
    },
    []
  );

  const overlappingRangesErrors = getOverlappingRangesErrors(openingHours);

  return errors.concat(overlappingRangesErrors);
}

export function validateOpeningHoursByDay(openingHoursByDay) {
  const days = Object.keys(openingHoursByDay);

  return days.reduce(
    function(memo, day) {
      const openingHours = openingHoursByDay[day];
      const errors = validateOpeningHours(openingHours).map(error => ({
        ...error,
        path: `${day}.${error.path}`
      }));

      return memo.concat(errors);
    },
    []
  );
}

export function extractHoursAndMinutes(str) {
  const pieces = str.split(':');
  return {
    hours: parseInt(pieces[0], 10),
    minutes: parseInt(pieces[1], 10)
  };
}

function leftPad(n, len) {
  return new Array(len - String(n).length + 1).join('0').concat(n);
}

export function formatTimeLong(timeStr) {
  if (timeStr === '24:00') {
    return 'midnight';
  }

  const [hourStr, minuteStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);

  const postFix = hour < 12 ? 'am' : 'pm';
  const hourFormated = `${leftPad(hour % 12 || 12, 2)}`;
  const minuteFormated = `:${minuteStr}`;

  return `${hourFormated}${minuteFormated} ${postFix}`;
}

export function formatTime(timeStr) {
  if (timeStr === '24:00') {
    return 'midnight';
  }

  const [hourStr, minuteStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);

  const postFix = hour < 12 ? 'am' : 'pm';
  const hourFormated = `${hour % 12 || 12}`;
  const minuteFormated = minuteStr === '00' ? '' : `:${minuteStr}`;

  return `${hourFormated}${minuteFormated}${postFix}`;
}

// Table related functions

export function getOpeningHoursOfDate(
  date,
  openingHoursByDay,
  specialOpeningHours = []
) {
  const day = date.getDayString().toLowerCase();

  const special = _.find(specialOpeningHours, ({ start, end }) => {
    const startDate = TZDate.createFromDateAndTimeString(start, '00:00');
    const endDate = TZDate.createFromDateAndTimeString(end, '23:59');

    return startDate.isSameDay(date) ||
      endDate.isSameDay(date) ||
      date.isBetween(startDate, endDate);
  });

  return special ? special : openingHoursByDay[day];
}

function isSpecialEndCase(openingHours) {
  if (!openingHours.length) {
    return false;
  }

  const { from, to } = openingHours[openingHours.length - 1];

  return from !== '00:00' && to === '24:00';
}

function isSpecialStartCase(openingHours) {
  if (!openingHours.length) {
    return false;
  }

  const { from, to } = openingHours[0];

  return from === '00:00' && to !== '24:00';
}

export function getNormalizedOpeningHours(
  date,
  openingHoursByDay,
  specialOpeningHours = []
) {
  const prevDay = date.addDays(-1);
  const nextDay = date.addDays(1);

  const openingHours = getOpeningHoursOfDate(
    date,
    openingHoursByDay,
    specialOpeningHours
  ).openingHours || [];
  const prevOpeningHours = getOpeningHoursOfDate(
    prevDay,
    openingHoursByDay,
    specialOpeningHours
  ).openingHours || [];
  const nextOpeningHours = getOpeningHoursOfDate(
    nextDay,
    openingHoursByDay,
    specialOpeningHours
  ).openingHours || [];

  let removeFirst = isSpecialEndCase(prevOpeningHours) &&
    isSpecialStartCase(openingHours);
  let addNext = isSpecialEndCase(openingHours) &&
    isSpecialStartCase(nextOpeningHours);

  const openingHoursNormalized = [...openingHours];

  if (removeFirst) {
    openingHoursNormalized.shift();
  }

  if (addNext) {
    openingHoursNormalized[openingHoursNormalized.length - 1] = Object.assign(
      {},
      openingHoursNormalized[openingHoursNormalized.length - 1],
      { to: nextOpeningHours[0].to }
    );
  }

  return openingHoursNormalized;
}

export function normalizeTableOpeningHours(
  firstDayOfWeek,
  openingHoursByDay,
  specialOpeningHours = [],
  today = new TZDate()
) {
  let startingDayIdx = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ].indexOf(firstDayOfWeek);

  if (startingDayIdx === -1) {
    startingDayIdx = 1;
  }

  return _.times(7).map((val, idx) => {
    const normalizedIdx = idx + startingDayIdx;
    const date = today.addDays(normalizedIdx - today.getDay());

    const { note } = getOpeningHoursOfDate(
      date,
      openingHoursByDay,
      specialOpeningHours
    );
    const normalizedOpeningHours = getNormalizedOpeningHours(
      date,
      openingHoursByDay,
      specialOpeningHours
    );
    const day = date.getDayString().toLowerCase();

    return {
      openingHours: normalizedOpeningHours,
      note,
      day
    };
  });
}



// WEBPACK FOOTER //
// ./src/commons/services/openingHours.js